import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, IconButton
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';
import WhatsAppSendButton from '../common/WhatsAppSendButton';

const GREEN  = '#1b4332';
const ORANGE = '#f59e0b';

const HDR_SX = {
  fontWeight: 700, color: '#fff', fontSize: '0.8rem', textTransform: 'uppercase',
  background: `linear-gradient(135deg, ${ORANGE} 0%, #ffa726 100%)`,
  borderRight: '1px solid rgba(255,255,255,0.2)', py: 1.5, px: 1,
  textAlign: 'center',
};
const CELL_SX = { py: 1.2, px: 1, fontSize: '0.82rem', textAlign: 'center' };

function fmtNum(v, d = 0) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}

function isSummaryRow(row) {
  const v = String(row.MARKET || '').trim().toUpperCase();
  return v.startsWith('TOTAL') || v.startsWith('MONTH TOTAL') || v.startsWith('LOCAL') || v.startsWith('GRAND MONTH');
}

function isTotalRow(row) {
  return String(row.SIZE || '').trim().toUpperCase() === 'TOTAL';
}

function isGrandRow(row) {
  return String(row.SIZE || '').trim().toUpperCase() === 'GRAND TOTAL';
}

// RS[0] — container summary
function ContainerSummaryTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;
  return (
    <TableContainer sx={{ maxHeight: 520, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow>
            {['NO','MARKET','CONTAINER','ORDER NO','PCS','CBM'].map(h => (
              <TableCell key={h} sx={HDR_SX}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const summary = isSummaryRow(row);
            return (
              <TableRow key={i} sx={{
                backgroundColor: summary ? '#fef3c7' : (i % 2 === 0 ? '#fff' : '#f8faf9'),
                '&:hover': { backgroundColor: summary ? '#fef3c7' : '#e8f5e9' },
              }}>
                <TableCell sx={{ ...CELL_SX, color: summary ? '#92400e' : '#555' }}>{summary ? '' : (row.ROW_NO || '')}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: summary ? 700 : 500, color: summary ? '#92400e' : '#333' }}>{row.MARKET || '—'}</TableCell>
                <TableCell sx={{ ...CELL_SX, color: summary ? '#92400e' : '#555' }}>{row.CONTAINER_NO || '—'}</TableCell>
                <TableCell sx={{ ...CELL_SX, color: summary ? '#92400e' : '#555' }}>{row.SUBORDERNO || '—'}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: summary ? 700 : 600, color: summary ? '#92400e' : '#2e7d32' }}>{fmtNum(row.PCS, 0)}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: summary ? 700 : 600, color: summary ? '#92400e' : '#2e7d32' }}>{fmtNum(row.CBM, 3)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// RS[1] — pallet detail
function PalletDetailTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;
  return (
    <TableContainer sx={{ maxHeight: 520, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 1050 }}>
        <TableHead>
          <TableRow>
            {['NO','ORDER NO','CONTAINER','MARKET','MARKET NAME','PALLET','SIZE','PCS','CBM'].map(h => (
              <TableCell key={h} sx={HDR_SX}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const total = isTotalRow(row);
            const grand = isGrandRow(row);
            const clr   = grand ? '#14532d' : total ? '#92400e' : null;
            return (
              <TableRow key={i} sx={{
                backgroundColor: grand ? '#dcfce7' : total ? '#fef3c7' : (i % 2 === 0 ? '#fff' : '#f8faf9'),
                '&:hover': { backgroundColor: grand ? '#dcfce7' : total ? '#fef3c7' : '#e8f5e9' },
              }}>
                <TableCell sx={{ ...CELL_SX, color: clr || '#555' }}>{(total || grand) ? '' : (row.ROW_NO || '')}</TableCell>
                <TableCell sx={{ ...CELL_SX, color: clr || '#555' }}>{(total || grand) ? '' : (row.SUBORDERNO || '—')}</TableCell>
                <TableCell sx={{ ...CELL_SX, color: clr || '#555' }}>{(total || grand) ? '' : (row.CONTAINER_NO || '—')}</TableCell>
                <TableCell sx={{ ...CELL_SX, color: clr || '#555' }}>{(total || grand) ? '' : (row.MARKET || '—')}</TableCell>
                <TableCell sx={{ ...CELL_SX, color: clr || '#555' }}>{(total || grand) ? '' : (row.MARKET_NAME || '—')}</TableCell>
                <TableCell sx={{ ...CELL_SX, color: clr || '#555' }}>{(total || grand) ? '' : (row.PALLET_NO || '—')}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: (total || grand) ? 700 : 500, color: clr || '#333' }}>{row.SIZE || '—'}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: (total || grand) ? 700 : 600, color: clr || '#2e7d32' }}>{fmtNum(row.PCS, 0)}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: (total || grand) ? 700 : 600, color: clr || '#2e7d32' }}>{fmtNum(row.CBM, 3)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// RS[3] — local sale detail
function LocalSaleTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No local sale data for this date</Alert>;
  return (
    <TableContainer sx={{ maxHeight: 520, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 600 }}>
        <TableHead>
          <TableRow>
            {['PALLET NO','THICKNESS','SIZE SET','QUALITY','PCS','CBM'].map(h => (
              <TableCell key={h} sx={HDR_SX}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{
              backgroundColor: i % 2 === 0 ? '#fff' : '#f8faf9',
              '&:hover': { backgroundColor: '#e8f5e9' },
            }}>
              <TableCell sx={CELL_SX}>{row.PALLETNO || '—'}</TableCell>
              <TableCell sx={CELL_SX}>{fmtNum(row.THICKNESS, 2)}</TableCell>
              <TableCell sx={CELL_SX}>{row.SIZESET || '—'}</TableCell>
              <TableCell sx={CELL_SX}>{row.QUALITY || '—'}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: '#2e7d32' }}>{fmtNum(row.PCS, 0)}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: '#2e7d32' }}>{fmtNum(row.CBM, 3)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const TAB_LABELS = ['Container Summary', 'Pallet Detail', 'Local Sale'];

const DailyLoadingSummaryView = ({ onBackClick, user }) => {
  const [date, setDate]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [data, setData]     = useState(null);
  const [tab, setTab]       = useState(0);

  const fetchData = async () => {
    if (!date) { setError('Please select a date'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/order/dailyloadingsummary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: date.format('YYYY-MM-DD') }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.data || apiData.data.every(rs => rs.length === 0)) {
        setError('No data found for the selected date.');
        return;
      }
      setData(apiData.data);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    if (data[0]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[0]), 'Container Summary');
    const palletRows = [...(data[1] || []), ...(data[2] || [])];
    if (palletRows.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(palletRows), 'Pallet Detail');
    if (data[3]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[3]), 'Local Sale');
    XLSX.writeFile(wb, `Loading_Summary_${date.format('YYYY-MM-DD')}.xlsx`);
  };

  // Merge RS[1] + RS[2] for the pallet detail tab
  const palletRows = data ? [...(data[1] || []), ...(data[2] || [])] : [];

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(27,67,50,0.1)', border: '1px solid #e8f5e9' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: GREEN }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ background: `linear-gradient(135deg, ${GREEN} 0%, #2d6a4f 100%)`, borderRadius: 2, p: 1.5, mr: 2 }}>
              <LocalShippingIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: GREEN }}>Daily Loading Summary</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>By date</Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
              <DatePicker label="Loading Date" value={date} onChange={setDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1, maxWidth: 300 } } }} />
              <Button variant="contained" size="large" startIcon={<SearchIcon />} onClick={fetchData}
                disabled={loading} sx={{ minWidth: 140, backgroundColor: GREEN, '&:hover': { backgroundColor: '#0f2419' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                Search
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: GREEN }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #e8f5e9' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="inherit"
              sx={{ '& .MuiTab-root': { fontWeight: 600, fontSize: '0.85rem' }, '& .Mui-selected': { color: GREEN }, '& .MuiTabs-indicator': { backgroundColor: GREEN } }}>
              {TAB_LABELS.map((l, i) => <Tab key={i} label={l} />)}
            </Tabs>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
              sx={{ color: GREEN, fontWeight: 600, textTransform: 'none' }}>Excel</Button>
          </Box>
          {tab === 0 && <ContainerSummaryTable rows={data[0]} />}
          {tab === 1 && <PalletDetailTable rows={palletRows} />}
          {tab === 2 && <LocalSaleTable rows={data[3]} />}
        </Card>
      )}

      {/* WhatsApp send button — shown below the report after data loads */}
      {data && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <WhatsAppSendButton
            user={user}
            label="Send Loading Report to WhatsApp"
            apiUrl={date ? `${API_URL}/api/loading/test?date=${date.format('YYYY-MM-DD')}` : null}
            disabled={!date}
          />
        </Box>
      )}
    </Container>
  );
};

export default DailyLoadingSummaryView;
